import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom';
import Dashboard from './modules/dashboard/Dashboard.jsx';
import Orders from './modules/orders/Orders.jsx';
import Trips from './modules/dispatch/Trips.jsx';
import CreateTripPage from './modules/dispatch/CreateTripPage.jsx';
import Accounting from './modules/accounting/Accounting.jsx';
import Billing from './modules/billing/Billing.jsx';
import Integration from './modules/integration/Integration.jsx';
import ControlPlaceholder from './modules/control/ControlPlaceholder.jsx';
import CustomerList from './modules/control/CustomerList.jsx';
import LocationList from './modules/control/LocationList.jsx';
import CarrierList from './modules/control/CarrierList.jsx';
import DriverList from './modules/control/DriverList.jsx';
import TruckList from './modules/control/TruckList.jsx';
import TrailerList from './modules/control/TrailerList.jsx';
import FleetList from './modules/control/FleetList.jsx';
import AccessoryList from './modules/control/AccessoryList.jsx';
import OwnerOperatorList from './modules/control/OwnerOperatorList.jsx';
import EmployeeList from './modules/control/EmployeeList.jsx';
import VendorList from './modules/control/VendorList.jsx';
import CustomsBrokerList from './modules/control/CustomsBrokerList.jsx';
import { CONTROL_SECTIONS } from './controlItems.jsx';
import Reports from './modules/reports/Reports.jsx';
import Utilities from './modules/utilities/Utilities.jsx';
import Fleet from './modules/fleet/Fleet.jsx';
import Compliance from './modules/compliance/Compliance.jsx';
import Marketplace from './modules/marketplace/Marketplace.jsx';
import Settings from './modules/settings/Settings.jsx';
import Login from './modules/auth/Login.jsx';
import OnboardingWizard from './modules/onboarding/OnboardingWizard.jsx';
import Header from './components/Header.jsx';
import OutsourcingOnly from './components/OutsourcingOnly.jsx';
import { useFeature, setFeatures, resetFeatures } from './features.js';
import { SESSION_EXPIRED_EVENT, clearStoredSession, storedTokenExpired } from './api.js';

const SESSION_EXPIRED_NOTICE = 'Your session has expired — please log in again.';

const Icon = {
  dashboard: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1"/><rect x="14" y="3" width="7" height="5" rx="1"/><rect x="14" y="12" width="7" height="9" rx="1"/><rect x="3" y="16" width="7" height="5" rx="1"/></svg>,
  orders: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="8" y1="16" x2="13" y2="16"/></svg>,
  trips: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="6" width="15" height="12" rx="1"/><path d="M16 10h4l3 3v5h-7z"/><circle cx="6" cy="20" r="2"/><circle cx="18" cy="20" r="2"/></svg>,
  accounting: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><line x1="7" y1="9" x2="17" y2="9"/><line x1="7" y1="13" x2="13" y2="13"/><line x1="7" y1="17" x2="15" y2="17"/></svg>,
  billing: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><line x1="6" y1="15" x2="10" y2="15"/></svg>,
  integration: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="9" width="6" height="6" rx="1"/><rect x="16" y="9" width="6" height="6" rx="1"/><path d="M8 12h8"/></svg>,
  control: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="4" width="18" height="18" rx="2"/><line x1="4" y1="10" x2="20" y2="10"/><circle cx="8" cy="7" r="1"/></svg>,
  reports: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18"/><path d="M7 15l4-5 3 3 5-7"/></svg>,
  utilities: () => <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a4 4 0 10-8.4 8.4L3 18l3 3 3.3-3.3a4 4 0 108.4-8.4z"/></svg>,
  collapse: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 6 9 12 15 18"/></svg>,
  expand: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18"/></svg>,
  list: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
  filePlus: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="12" x2="12" y2="18"/><line x1="9" y1="15" x2="15" y2="15"/></svg>,
  upload: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  route: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="6" cy="19" r="2"/><circle cx="18" cy="5" r="2"/><path d="M8 19h6a4 4 0 000-8H10a4 4 0 010-8h6"/></svg>,
  layers: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  repeat: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>,
  fleet: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19V6a2 2 0 012-2h8l6 6v9a2 2 0 01-2 2H6a2 2 0 01-2-2z"/><circle cx="16" cy="15" r="2"/></svg>,
  compliance: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4"/><path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6z"/></svg>,
  marketplace: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.5" y2="16.5"/></svg>,
  settings: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 00.34 1.9l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.7 1.7 0 00-1.9-.34 1.7 1.7 0 00-1 1.55V21a2 2 0 01-4 0v-.09a1.7 1.7 0 00-1-1.55 1.7 1.7 0 00-1.9.34l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.7 1.7 0 00.34-1.9 1.7 1.7 0 00-1.55-1H3a2 2 0 010-4h.09a1.7 1.7 0 001.55-1 1.7 1.7 0 00-.34-1.9l-.06-.06a2 2 0 112.83-2.83l.06.06a1.7 1.7 0 001.9.34H9a1.7 1.7 0 001-1.55V3a2 2 0 014 0v.09a1.7 1.7 0 001 1.55 1.7 1.7 0 001.9-.34l.06-.06a2 2 0 112.83 2.83l-.06.06a1.7 1.7 0 00-.34 1.9V9a1.7 1.7 0 001.55 1H21a2 2 0 010 4h-.09a1.7 1.7 0 00-1.55 1z"/></svg>
};

// Sub-items shown in a flyout when clicking a nav item. `disabled: true`
// items are shown grayed-out with a "Soon" tag rather than as dead links —
// only real, built destinations are actually clickable.
const SUBMENUS = {
  '/orders': [
    { label: 'All orders', to: '/orders', icon: Icon.list },
    { label: 'New order', to: '/orders?new=1', icon: Icon.filePlus },
    { label: 'Bulk import', icon: Icon.upload, disabled: true },
    { label: 'Route & mileage', icon: Icon.route, disabled: true },
    { label: 'Order templates', icon: Icon.layers, disabled: true },
    { label: 'Recurring orders', icon: Icon.repeat, disabled: true }
  ],
  '/control': CONTROL_SECTIONS
};


const NAV = [
  { to: '/', label: 'Dashboard', end: true, icon: Icon.dashboard },
  { to: '/orders', label: 'Orders', icon: Icon.orders },
  { to: '/trips', label: 'Trips', icon: Icon.trips },
  { to: '/accounting', label: 'Accounting', icon: Icon.accounting },
  { to: '/billing', label: 'Billing', icon: Icon.billing },
  { to: '/integration', label: 'Integration', icon: Icon.integration },
  { to: '/control', label: 'Control', icon: Icon.control, flyoutOnly: true },
  { to: '/reports', label: 'Reports', icon: Icon.reports },
  { to: '/utilities', label: 'Utilities', icon: Icon.utilities }
];

function ThemeToggle({ theme, onToggle }) {
  return (
    <button className="theme-toggle" onClick={onToggle} title="Toggle light/dark" aria-label="Toggle theme">
      {theme === 'dark' ? (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4"/><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></svg>
      ) : (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>
      )}
    </button>
  );
}

// Flyout opens on hover OR click, and — per the request — stays open until
// the user clicks a value inside it or clicks anywhere outside it. It no
// longer closes just because the mouse leaves.
function NavItemWithFlyout({ n, collapsed }) {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [anchorTop, setAnchorTop] = useState(0);
  const [anchorHeight, setAnchorHeight] = useState(44);
  const wrapperRef = useRef(null);
  // Control > Carrier belongs to the 'outsourcing' feature — drop it from
  // the flyout for tenants that don't have it.
  const outsourcing = useFeature('outsourcing');
  const submenu = n.to === '/control' && !outsourcing
    ? SUBMENUS[n.to].map(g => ({ ...g, items: g.items.filter(i => i.label !== 'Carrier') }))
    : SUBMENUS[n.to];

  function measure() {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setAnchorTop(rect.top);
      setAnchorHeight(rect.height);
    }
  }

  function handleMouseEnter() {
    measure();
    setOpen(true);
  }

  function handleMouseLeave() {
    if (!pinned) setOpen(false);
  }

  function handleClick(e) {
    if (n.flyoutOnly) e.preventDefault();
    measure();
    setPinned(true);
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function onOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target) &&
          !e.target.closest('.nav-flyout')) {
        setOpen(false);
        setPinned(false);
      }
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const sidebarWidth = collapsed ? 68 : 225;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}
         onMouseEnter={submenu ? handleMouseEnter : undefined}
         onMouseLeave={submenu ? handleMouseLeave : undefined}>
      <NavLink to={n.to} end={n.end}
               onClick={submenu ? handleClick : undefined}
               className={({isActive}) => 'navitem' + (isActive ? ' active' : '') + (open ? ' flyout-open' : '')}
               title={collapsed ? n.label : undefined}>
        <n.icon />
        {!collapsed && <span>{n.label}</span>}
      </NavLink>

      {submenu && open && (
        <div className="nav-flyout" style={{ left: sidebarWidth }}
             onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {/* No connector piece — the flyout panel starts flush at the
              sidebar edge, and the active item's own squared-off right
              corner (see .navitem.flyout-open) is what makes it read as
              one continuous shape. */}
          <div className="nav-flyout-content" style={{ paddingTop: 20 }}>
            <div className="nav-flyout-header">{n.label}</div>
            {submenu[0]?.section ? (
              // Grouped format: [{ section, items }] — renders a subheader
              // per group, e.g. the Control panel's Fleets/Personal
              // Profiles/Others sections.
              submenu.map(group => (
                <div key={group.section} className="nav-flyout-group">
                  <div className="nav-flyout-subheader">{group.section}</div>
                  {group.items.map(s => (
                    s.disabled ? (
                      <div key={s.label} className="nav-flyout-item disabled">
                        <s.icon />
                        <span>{s.label}</span>
                        <span className="nav-flyout-soon">Soon</span>
                      </div>
                    ) : (
                      <NavLink key={s.label} to={s.to} className="nav-flyout-item" onClick={() => { setOpen(false); setPinned(false); }}>
                        <s.icon />
                        <span>{s.label}</span>
                      </NavLink>
                    )
                  ))}
                </div>
              ))
            ) : (
              // Flat format: [{ label, to, icon }] — the original shape,
              // still used by Orders and anything else with a short list.
              submenu.map(s => (
                s.disabled ? (
                  <div key={s.label} className="nav-flyout-item disabled">
                    <s.icon />
                    <span>{s.label}</span>
                    <span className="nav-flyout-soon">Soon</span>
                  </div>
                ) : (
                  <NavLink key={s.label} to={s.to} className="nav-flyout-item" onClick={() => { setOpen(false); setPinned(false); }}>
                    <s.icon />
                    <span>{s.label}</span>
                  </NavLink>
                )
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  const [theme, setTheme] = useState('dark');
  const [collapsed, setCollapsed] = useState(false);
  const [impersonating, setImpersonating] = useState(null);
  const [loginNotice, setLoginNotice] = useState('');

  useEffect(() => {
    // Admin "View as this client" hands off a short-lived token via URL
    // params (can't write directly to this domain's localStorage from
    // stsgp.com — different origin). Pick it up once, store it the same
    // way a normal login does, then scrub the URL so the token isn't
    // sitting in the address bar or browser history.
    const params = new URLSearchParams(window.location.search);
    const impersonateToken = params.get('impersonate_token');
    const impersonateUserParam = params.get('impersonate_user');
    if (impersonateToken && impersonateUserParam) {
      try {
        const impersonatedUser = JSON.parse(atob(decodeURIComponent(impersonateUserParam)));
        resetFeatures();
        localStorage.setItem('loadline_token', impersonateToken);
        localStorage.setItem('loadline_user', JSON.stringify(impersonatedUser));
        localStorage.setItem('loadline_impersonating', 'true');
        window.history.replaceState({}, '', window.location.pathname);
      } catch (e) {
        // Malformed handoff params — fall through to normal auth check below.
      }
    }

    // An invite / password-reset link (?reset_token=) always gets the
    // set-password screen. Any session already stored in this browser
    // (possibly someone else's, possibly expired) would otherwise take over
    // and hide it.
    if (params.get('reset_token')) clearStoredSession();

    const token = localStorage.getItem('loadline_token');
    const savedUser = localStorage.getItem('loadline_user');
    if (token && savedUser) {
      // Stored login already past its 12h expiry: don't resume into a
      // screen where every request fails — go to login instead.
      if (storedTokenExpired(token)) {
        clearStoredSession();
        setLoginNotice(SESSION_EXPIRED_NOTICE);
      } else {
        setUser(JSON.parse(savedUser));
      }
    }
    setImpersonating(localStorage.getItem('loadline_impersonating') === 'true');
    const savedTheme = localStorage.getItem('loadline_theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
    const savedCollapsed = localStorage.getItem('loadline_sidebar_collapsed') === 'true';
    setCollapsed(savedCollapsed);
    setChecked(true);
  }, []);

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    document.documentElement.setAttribute('data-theme', next);
    localStorage.setItem('loadline_theme', next);
  }

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('loadline_sidebar_collapsed', String(next));
  }

  // Any API call rejected with 401 mid-session (api.js clears the stored
  // session) — back to the login screen with an explanation.
  useEffect(() => {
    const onExpired = () => {
      resetFeatures();
      setUser(null);
      setImpersonating(false);
      setLoginNotice(SESSION_EXPIRED_NOTICE);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  function handleLogin(u, features) {
    setLoginNotice('');
    // Login response carries the tenant's enabled features; otherwise they
    // load from /auth/me on first use.
    if (Array.isArray(features)) setFeatures(features); else resetFeatures();
    localStorage.setItem('loadline_user', JSON.stringify(u));
    setUser(u);
  }

  // The onboarding wizard's last step already PATCHed onboardingCompleted
  // on the server — this just updates local state/localStorage to match,
  // so the gate below re-renders straight into the dashboard without
  // needing a fresh login.
  function handleOnboardingComplete() {
    const updated = { ...user, onboardingCompleted: true };
    localStorage.setItem('loadline_user', JSON.stringify(updated));
    setUser(updated);
  }

  function logout() {
    resetFeatures();
    localStorage.removeItem('loadline_token');
    localStorage.removeItem('loadline_user');
    localStorage.removeItem('loadline_impersonating');
    setUser(null);
    setImpersonating(false);
  }

  // Auto-logout after 1 hour of no activity — resets on any mouse, keyboard,
  // scroll, or touch interaction, so an actively-used session never hits
  // this even if it runs well past an hour. Separate from the token's own
  // fixed 12h expiry on the server, which still applies regardless.
  useEffect(() => {
    if (!user) return;
    const TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
    let timer;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(logout, TIMEOUT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetTimer));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [user]);

  if (!checked) return null;
  if (!user) return <Login onLogin={handleLogin} notice={loginNotice} />;
  // Checks for an explicit `false`, not just falsy — a session saved to
  // localStorage before this feature existed has no onboardingCompleted
  // key at all (undefined), and that must NOT be treated as "needs
  // onboarding" or every already-established tenant would suddenly see
  // this wizard on their next page load. Also skipped while impersonating
  // — an admin viewing a client's account for support shouldn't be forced
  // through that client's own first-login setup.
  if (!impersonating && user.onboardingCompleted === false) {
    return <OnboardingWizard user={user} onComplete={handleOnboardingComplete} />;
  }

  return (
    <BrowserRouter>
      {impersonating && (
        <div style={{
          background: 'var(--amber)', color: '#1a1f28', textAlign: 'center',
          padding: '8px 16px', fontSize: 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12
        }}>
          <span>Viewing as {user.name} — Admin Mode</span>
          <button onClick={logout} style={{ background: '#1a1f28', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
            Exit
          </button>
        </div>
      )}
      <div className={`shell ${collapsed ? 'sidebar-collapsed' : ''}`}>
        <aside className="sidebar">
          <div className="brand">
            {!collapsed && <span>TruckFlow</span>}
            <div style={{display:'flex', gap:6, alignItems:'center', marginLeft: collapsed ? 0 : 'auto'}}>
              {!collapsed && <ThemeToggle theme={theme} onToggle={toggleTheme} />}
              <button className="theme-toggle" onClick={toggleCollapsed} title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                {collapsed ? <Icon.expand /> : <Icon.collapse />}
              </button>
            </div>
          </div>
          <nav>
            {NAV.map(n => <NavItemWithFlyout key={n.to} n={n} collapsed={collapsed} />)}
          </nav>
        </aside>
        <div className="content-col">
          <Header onLogout={logout} />
          <main className="main">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/trips" element={<Trips />} />
              <Route path="/orders/:loadId/create-trip" element={<CreateTripPage />} />
              <Route path="/accounting" element={<Accounting />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/integration" element={<Integration />} />
              <Route path="/control/customers" element={<CustomerList />} />
              <Route path="/control/locations" element={<LocationList />} />
              <Route path="/control/carrier" element={<OutsourcingOnly><CarrierList /></OutsourcingOnly>} />
              <Route path="/control/drivers" element={<DriverList />} />
              <Route path="/control/trucks" element={<TruckList />} />
              <Route path="/control/trailers" element={<TrailerList />} />
              <Route path="/control/fleets" element={<FleetList />} />
              <Route path="/control/accessories" element={<AccessoryList />} />
              <Route path="/control/owner-operators" element={<OwnerOperatorList />} />
              <Route path="/control/employees" element={<EmployeeList />} />
              <Route path="/control/vendors" element={<VendorList />} />
              <Route path="/control/customs-brokers" element={<CustomsBrokerList />} />
              <Route path="/control/:slug" element={<ControlPlaceholder />} />
              <Route path="/reports" element={<Reports />} />
              <Route path="/utilities" element={<Utilities />} />
              <Route path="/fleet" element={<Fleet />} />
              <Route path="/compliance" element={<Compliance />} />
              <Route path="/marketplace" element={<Marketplace />} />
              <Route path="/settings" element={<Settings />} />
            </Routes>
          </main>
        </div>
      </div>
    </BrowserRouter>
  );
}
